import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { formatPrice } from '@shared/helpers';
import { useDateFnsLocale } from '@shared/hooks/useDateFnsLocale';
import { UiText } from '@shared/ui';

import cls from './OrderDetails.module.css';

import type { Order } from '@entities/order';

type OrderDetailsProps = {
    order: Order;
};

export const OrderDetails = ({ order }: OrderDetailsProps) => {
    const { t } = useTranslation();
    const dateLocale = useDateFnsLocale();

    return (
        <div className={cls.details}>
            <section className={cls.card}>
                <UiText tag="h2" variant="heading" weight="bold">
                    {t('orders.page.estimates.title')}
                </UiText>
                <div className={cls.tableScroll}>
                    <table className={cls.table}>
                        <thead>
                            <tr>
                                <th scope="col">{t('orders.page.table.estimate')}</th>
                                <th scope="col" className={cls.numberColumn}>
                                    {t('orders.page.table.negotiatedPrice')}
                                </th>
                                <th scope="col" className={cls.numberColumn}>
                                    {t('orders.page.table.completed')}
                                </th>
                                <th scope="col" className={cls.numberColumn}>
                                    {t('orders.page.table.paid')}
                                </th>
                                <th scope="col" className={cls.numberColumn}>
                                    {t('orders.page.table.toBePaid')}
                                </th>
                                <th scope="col" className={cls.currencyColumn}>
                                    {t('orders.page.table.currency')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {order.estimates.map((estimate) => (
                                <tr key={estimate.id}>
                                    <td>{estimate.name}</td>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(estimate.negotiatedPrice, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(estimate.completed, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(estimate.paid, { maximumFractionDigits: 2 })}
                                    </td>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(estimate.toBePaid, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                    <td className={cls.currencyColumn}>
                                        {estimate.currency.shortCode}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {order.totals.map((total) => (
                                <tr key={total.currency.id}>
                                    <th scope="row">{t('orders.page.table.total')}</th>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(total.negotiatedPrice, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(total.completed, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(total.paid, { maximumFractionDigits: 2 })}
                                    </td>
                                    <td className={cls.numberColumn}>
                                        {formatPrice(total.toBePaid, {
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                    <td className={cls.currencyColumn}>
                                        {total.currency.shortCode}
                                    </td>
                                </tr>
                            ))}
                        </tfoot>
                    </table>
                </div>
            </section>

            <section className={cls.card}>
                <UiText tag="h2" variant="heading" weight="bold">
                    {t('orders.page.payments.title')}
                </UiText>
                {order.payments.length === 0 ? (
                    <UiText>{t('orders.page.payments.empty')}</UiText>
                ) : (
                    <div className={cls.tableScroll}>
                        <table className={cls.table}>
                            <thead>
                                <tr>
                                    <th scope="col" className={cls.dateColumn}>
                                        {t('orders.page.table.date')}
                                    </th>
                                    <th scope="col">{t('orders.page.table.estimate')}</th>
                                    <th scope="col" className={cls.numberColumn}>
                                        {t('orders.page.table.amount')}
                                    </th>
                                    <th scope="col" className={cls.currencyColumn}>
                                        {t('orders.page.table.currency')}
                                    </th>
                                    <th scope="col" className={cls.commentColumn}>
                                        {t('orders.page.table.comment')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.payments.map((payment) => (
                                    <tr key={payment.id}>
                                        <td className={cls.dateColumn}>
                                            {format(new Date(payment.date), 'd MMMM yyyy', {
                                                locale: dateLocale,
                                            })}
                                        </td>
                                        <td>{payment.estimate.name}</td>
                                        <td className={cls.numberColumn}>
                                            {formatPrice(payment.amount, {
                                                maximumFractionDigits: 2,
                                            })}
                                        </td>
                                        <td className={cls.currencyColumn}>
                                            {payment.currency.shortCode}
                                        </td>
                                        <td className={cls.commentColumn}>{payment.comment}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                {order.paymentTotals.map((total) => (
                                    <tr key={total.currency.id}>
                                        <td aria-hidden="true">&nbsp;</td>
                                        <th scope="row">{t('orders.page.table.total')}</th>
                                        <td className={cls.numberColumn}>
                                            {formatPrice(total.amount, {
                                                maximumFractionDigits: 2,
                                            })}
                                        </td>
                                        <td className={cls.currencyColumn}>
                                            {total.currency.shortCode}
                                        </td>
                                        <td aria-hidden="true">&nbsp;</td>
                                    </tr>
                                ))}
                            </tfoot>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};
